/**
 * Formattazione di date, orari e byte per l'interfaccia.
 *
 * Vivono fuori da `App.tsx` perche' servono a schermate che il code splitting
 * per ruolo tiene in chunk diversi (PERF-04): il pannello coach e l'editor
 * degli orari li importano dal proprio chunk, l'atleta dal suo.
 */

export function formatTime(value: string, timeZone?: string) {
  return new Intl.DateTimeFormat("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(value));
}

export function formatDay(value: string, timeZone?: string) {
  return new Intl.DateTimeFormat("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(value));
}

export function formatWeekday(value: string) {
  return new Intl.DateTimeFormat("it-IT", { weekday: "short" }).format(new Date(value));
}

export function formatDateOnly(value: string) {
  return new Intl.DateTimeFormat("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(`${value}T12:00:00`));
}

export function dateInTimeZone(value: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function addPlainDateDays(value: string, days: number) {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function isoWeekday(value: string) {
  const weekday = new Date(`${value}T12:00:00Z`).getUTCDay();
  return weekday === 0 ? 7 : weekday;
}

export function startOfIsoWeek(value: string) {
  return addPlainDateDays(value, 1 - isoWeekday(value));
}

export function instantAtZonedDateTime(value: string, time: string, timeZone: string) {
  const [year = 1970, month = 1, day = 1] = value.split("-").map(Number);
  const [hour = 0, minute = 0] = time.split(":").map(Number);
  const desired = Date.UTC(year, month - 1, day, hour, minute, 0);
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  let instant = desired;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = formatter.formatToParts(new Date(instant));
    const numberPart = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((candidate) => candidate.type === type)?.value);
    const observed = Date.UTC(
      numberPart("year"),
      numberPart("month") - 1,
      numberPart("day"),
      numberPart("hour"),
      numberPart("minute"),
      numberPart("second"),
    );
    const correction = desired - observed;
    instant += correction;
    if (correction === 0) break;
  }
  return new Date(instant).toISOString();
}

export function shortWeekday(value: string) {
  return new Intl.DateTimeFormat("it-IT", { weekday: "short", timeZone: "UTC" })
    .format(new Date(`${value}T12:00:00Z`))
    .replace(".", "");
}

export function plainDateDay(value: string) {
  return String(Number(value.slice(8, 10)));
}

/**
 * Dove cade una data rispetto a oggi, detto una volta sola per testate, celle e
 * selettore giorni: due predicati separati per la stessa domanda finirebbero per
 * divergere di un giorno al primo cambio di fuso. Le date sono ISO, quindi il
 * confronto lessicografico è cronologico.
 */
export function dayClassName(date: string, today: string) {
  if (date < today) return "is-past";
  if (date === today) return "is-today";
  return "";
}

/**
 * `Intl` in italiano scrive le date in minuscolo ("lunedì 27 luglio"); come
 * titolo di pagina servono le iniziali maiuscole, che il CSS `capitalize` non
 * può dare senza toccare anche i titoli che non sono date.
 */
export function capitalizeWords(value: string) {
  return value.replace(/(^|\s)(\p{L})/gu, (_, space: string, letter: string) => {
    return space + letter.toUpperCase();
  });
}

export function weekdayName(day: number) {
  return (
    ["", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"][day] ?? ""
  );
}

export function formatBytes(value: number) {
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}
